/**
 * Public read-only view of a saved Lineup Sim run.
 * Renders the saved squad summary + FPL rank without exposing the wheel UI.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Crown, Trophy, SoccerBall } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LineupSimShare() {
    const { run_id } = useParams();
    const [doc, setDoc] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        axios.get(`${API}/lineup-sim/share/${run_id}`)
            .then((r) => setDoc(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Not found"));
    }, [run_id]);

    return (
        <AppShell>
            <section className="space-y-4" data-testid="share-page">
                <Link to="/lineup-sim" className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft size={11} weight="bold" /> Build your own squad
                </Link>
                {err && (
                    <div className="border border-red-400/30 bg-red-400/5 p-4 font-mono text-[11px] text-red-300"
                         data-testid="share-error">
                        {err}
                    </div>
                )}
                {doc && (
                    <>
                        <header className="border-2 border-fa-yellow/40 bg-gradient-to-br from-fa-yellow/10 via-transparent to-fa-mint/5 p-5">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                                <SoccerBall size={11} weight="fill" /> Lineup Sim · shared squad
                            </div>
                            <h1 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mt-1"
                                data-testid="share-label">
                                {doc.label}
                            </h1>
                            <div className="font-mono text-[10px] text-white/55 mt-1">
                                by <span className="text-white">{doc.user_name || "Guest"}</span>
                                {" · "}{doc.mode} mode
                                {doc.formation && <> · {doc.formation}</>}
                                {" · "}{doc.n_gws} GWs
                            </div>
                        </header>
                        {doc.fpl_rank && (
                            <div className="border-2 border-fa-yellow/40 bg-fa-yellow/[0.06] p-4 flex justify-between flex-wrap gap-2"
                                 data-testid="share-rank">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow flex items-center gap-1.5">
                                        <Trophy size={11} weight="fill" /> FPL rank estimate
                                    </div>
                                    <div className="font-display font-black text-2xl text-fa-yellow mt-1">{doc.fpl_rank.label}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Avg total</div>
                                    <div className="font-display font-black text-3xl text-fa-mint tabular-nums">{doc.total?.avg}p</div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">better than {doc.fpl_rank.percentile}%</div>
                                </div>
                            </div>
                        )}
                        {doc.squad?.length > 0 && (
                            <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-white/[0.02] border-b border-white/10">
                                        <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                                            <th className="px-2 py-2">Pos</th>
                                            <th className="px-2 py-2">Player</th>
                                            <th className="px-2 py-2">Team</th>
                                            <th className="px-2 py-2 text-right">£</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {doc.squad.map((p) => (
                                            <tr key={p.id} className="border-b border-white/5"
                                                data-testid={`share-squad-row-${p.id}`}>
                                                <td className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/55">{p.position}</td>
                                                <td className="px-2 py-1.5 font-display font-black text-sm">
                                                    {p.name}
                                                    {doc.captain_id === p.id && <span className="font-mono text-[8px] font-bold ml-1 text-fa-yellow">(C)</span>}
                                                </td>
                                                <td className="px-2 py-1.5 font-mono text-[10px] text-white/60">{p.team_name}</td>
                                                <td className="px-2 py-1.5 font-mono text-[10px] tabular-nums text-right">£{p.price}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {doc.bench?.length > 0 && (
                            <div className="border border-white/10 bg-white/[0.02] p-3" data-testid="share-bench">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">Bench (12–15)</div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                    {doc.bench.map((p) => (
                                        <div key={p.id} className="font-mono text-[10px] text-white/55 px-1.5 py-1 bg-black/30 border border-white/5">
                                            {p.name} · {p.position} · £{p.price}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </section>
        </AppShell>
    );
}
